import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { INDEXING_STATUS } from '@trustero/trustero-api-web/lib/common/model_pb'
import { ContentStorePromiseClient } from '@trustero/trustero-api-web/lib/contentstore/contentstore_grpc_web_pb'
import { GetContentIndexingStatusRequest } from '@trustero/trustero-api-web/lib/contentstore/contentstore_pb'
import { useSwrGrpc } from 'src/components/async/useSwrGrpc'

const POLL_INTERVAL_MS = 5000

export type ResolveIndexingStatus = (
  initial?: INDEXING_STATUS,
  contentId?: string,
) => INDEXING_STATUS | undefined

// Default (no provider): just echo the row's embedded first-paint status.
const defaultResolve: ResolveIndexingStatus = (initial) => initial

const IndexingStatusContext =
  createContext<ResolveIndexingStatus>(defaultResolve)

/**
 * Row components call this to get the status to render: the live polled
 * value when available, otherwise the embedded first-paint value.
 */
export const useResolvedIndexingStatus = (
  initial?: INDEXING_STATUS,
  contentId?: string,
): INDEXING_STATUS | undefined =>
  useContext(IndexingStatusContext)(initial, contentId)

const isInProgress = (s?: INDEXING_STATUS): boolean =>
  s === INDEXING_STATUS.INDEXING_STATUS_INDEXING

/**
 * useIndexingStatusResolver polls the shared
 * ContentStore.GetContentIndexingStatus RPC once per grid (batched over all
 * the grid's content ids), but only while something is still indexing —
 * judged by the resolved status (live value overriding the initial
 * value), so polling stops once everything reaches a terminal
 * state even though the un-refetched list payload still says "indexing".
 * The last seen statuses are retained so failed/indexed rows keep their
 * state after polling stops.
 */
export const useIndexingStatusResolver = (
  contentIds: string[],
  initialStatuses: (INDEXING_STATUS | undefined)[],
): ResolveIndexingStatus => {
  const idsKey = useMemo(
    () =>
      Array.from(new Set(contentIds.filter(Boolean)))
        .sort()
        .join(','),
    [contentIds],
  )
  const ids = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey])

  const initialStatusByContentId = useMemo(() => {
    const map = new Map<string, INDEXING_STATUS>()
    contentIds.forEach((cid, i) => {
      if (cid) {
        map.set(
          cid,
          initialStatuses[i] ?? INDEXING_STATUS.INDEXING_STATUS_UNSPECIFIED,
        )
      }
    })
    return map
  }, [contentIds, initialStatuses])

  const [liveByContentId, setLiveByContentId] = useState<
    Map<string, INDEXING_STATUS>
  >(new Map())

  const anyIndexing = useMemo(
    () =>
      ids.some((cid) =>
        isInProgress(
          liveByContentId.get(cid) ?? initialStatusByContentId.get(cid),
        ),
      ),
    [ids, liveByContentId, initialStatusByContentId],
  )

  const request = useMemo(
    () => new GetContentIndexingStatusRequest().setContentIdsList(ids),
    [ids],
  )

  const swrConfig = useMemo(
    () => ({ refreshInterval: anyIndexing ? POLL_INTERVAL_MS : 0 }),
    [anyIndexing],
  )

  const { response } = useSwrGrpc(
    ContentStorePromiseClient.prototype.getContentIndexingStatus,
    request,
    ids.length > 0 && anyIndexing,
    swrConfig,
  )

  const data = response.data
  useEffect(() => {
    if (!data) {
      return
    }
    const entries = data.getStatusesMap().getEntryList()
    if (entries.length === 0) {
      return
    }
    setLiveByContentId((prev) => {
      const next = new Map(prev)
      entries.forEach(([cid, status]) => next.set(cid, status))
      return next
    })
  }, [data])

  return useMemo<ResolveIndexingStatus>(
    () => (initial, contentId) =>
      (contentId ? liveByContentId.get(contentId) : undefined) ?? initial,
    [liveByContentId],
  )
}

export const IndexingStatusProvider = ({
  resolve,
  children,
}: {
  resolve: ResolveIndexingStatus
  children: React.ReactNode
}): JSX.Element => (
  <IndexingStatusContext.Provider value={resolve}>
    {children}
  </IndexingStatusContext.Provider>
)
